import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMe } from '@/lib/auth'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { primaryWallet } from '@/lib/api-types'
import { SOL_MINT, tokenLogo } from '@/lib/default-tokens'
import { formatSol, formatUsd, plural } from '@/lib/format'
import {
  PNL_RANGES,
  PNL_SOURCES,
  PNL_ZONE_LABEL,
  describeBounds,
  pnlRangeBounds,
  pnlTokens,
  pnlTotals,
  pnlTrades,
  readChoices,
  signedSol,
  signedUsd,
  writeChoices,
  type PnlRange,
  type PnlSource,
} from '@/lib/pnl'
import { WalletSetup } from '@/components/wallet-setup'
import { Dropdown } from '@/components/dropdown'
import { Segmented } from '@/components/segmented'
import { DetailList } from '@/components/detail-list'
import { TokenIcon } from '@/components/holdings-table'

const ALL_TOKENS = '__all__'

/**
 * P&L (John, Nov 2 2026): one token or all of them, over today, yesterday,
 * this week, last week or all time, from the strategies' orders, hand swaps
 * or both. Sold less bought, in dollars and in SOL, from the same trade
 * records as the Transactions table and the reports.
 */
export function PnlPage() {
  const trpc = useTRPC()
  const { user } = useMe()
  const wallet = primaryWallet(user)
  const ready = Boolean(wallet && user?.walletSetupComplete)
  const [remembered] = useState(readChoices)
  const [token, setToken] = useState<string>(remembered?.token ?? ALL_TOKENS)
  const [range, setRange] = useState<PnlRange>(remembered?.range ?? 'today')
  const [source, setSource] = useState<PnlSource>(remembered?.source ?? 'both')
  useEffect(() => writeChoices({ token: token === ALL_TOKENS ? null : token, range, source }), [token, range, source])
  // one slim request for every trade. The figures keep themselves current (John, Nov 4 2026: no refresh click): the
  // live feed reads this again the moment an order confirms (activity-feed keysOf), the page reads again on return to
  // the tab (the app's queries do not by default) and every minute meanwhile
  const history = useQuery({ ...trpc.swap.pnlTrades.queryOptions({ walletIndex: wallet?.index ?? 0 }), enabled: ready, staleTime: 15_000, refetchInterval: 60_000, refetchOnWindowFocus: true })
  const trades = useMemo(() => pnlTrades(history.data ?? []), [history.data])
  const tokens = useMemo(() => pnlTokens(trades), [trades])
  // the period's edges move with the clock: worked out as of the last refresh of the trades
  const refreshedAt = history.dataUpdatedAt
  const bounds = useMemo(() => pnlRangeBounds(range, refreshedAt ? new Date(refreshedAt) : undefined), [range, refreshedAt])
  const selected = token !== ALL_TOKENS && tokens.some((t) => t.key === token) ? token : null
  const totals = useMemo(() => pnlTotals(trades, { token: selected, source, ...bounds }), [trades, selected, source, bounds])

  if (!ready || !wallet) return <WalletSetup />
  const loading = history.isPending
  const tone = totals.netUsd > 0 ? 'text-positive' : totals.netUsd < 0 ? 'text-negative' : ''
  const tokenChoices = [
    { value: ALL_TOKENS, label: 'All tokens' },
    ...tokens.map((t) => ({
      value: t.key,
      label: (
        <>
          <TokenIcon symbol={t.symbol} imageUrl={t.imageUrl} size="size-5" />
          <span>{t.symbol}</span>
        </>
      ),
    })),
  ]
  const period = describeBounds(bounds)
  const orders = totals.sells + totals.buys

  return (
    <div className="grid max-w-3xl gap-6 *:min-w-0">
      <section className="card">
        <div className="grid gap-4 p-4 sm:grid-cols-3 sm:p-6">
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Token</span>
            <Dropdown value={token} options={tokenChoices} onChange={setToken} label="Token" disabled={loading} />
          </div>
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Period</span>
            <Dropdown value={range} options={PNL_RANGES} onChange={setRange} label="Period" />
          </div>
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Include</span>
            <Segmented value={source} options={PNL_SOURCES} onChange={setSource} />
          </div>
        </div>
        <div className="border-t border-line p-4 sm:p-6">
          {loading ? (
            <span className="skeleton inline-block h-10 w-44 align-middle" />
          ) : (
            <div className={`text-4xl font-semibold tabular-nums ${tone}`}>{signedUsd(totals.netUsd)}</div>
          )}
          <div className="mt-3 flex items-center gap-2.5 text-xl tabular-nums text-muted">
            <TokenIcon symbol="SOL" imageUrl={tokenLogo(SOL_MINT)} size="size-7" />
            {loading ? <span className="skeleton inline-block h-6 w-32" /> : signedSol(totals.netSol)}
          </div>
          <p className="mt-4 text-xs text-muted">{period ? `${period} · ${PNL_ZONE_LABEL}` : 'Every trade so far'}</p>
          {history.isError ? (
            <p className="mt-2 text-sm text-negative">
              {errorMessage(history.error, 'Your trades could not be loaded.')}{' '}
              <button type="button" className="underline" onClick={() => void history.refetch()}>
                Retry
              </button>
            </p>
          ) : !loading && orders === 0 ? (
            <p className="mt-2 text-sm text-muted">No trades in this period.</p>
          ) : null}
        </div>
        <div className="px-4 pb-1 text-sm sm:px-6">
          {loading ? (
            <div className="grid gap-3 border-t border-line py-3">
              <span className="skeleton h-5 w-2/3" />
              <span className="skeleton h-5 w-1/2" />
              <span className="skeleton h-5 w-1/3" />
            </div>
          ) : (
            <DetailList
              rows={[
                { label: 'Sold', value: formatUsd(totals.soldUsd), hint: `${formatSol(totals.soldSol)} SOL` },
                { label: 'Bought', value: formatUsd(totals.boughtUsd), hint: `${formatSol(totals.boughtSol)} SOL` },
                { label: 'Orders', value: `${plural(totals.sells, 'sell')}, ${plural(totals.buys, 'buy')}` },
              ]}
            />
          )}
          {!loading && totals.unpricedSol > 0 ? (
            <p className="py-2.5 text-xs text-muted">{plural(totals.unpricedSol, 'trade')} without a SOL price left out of the SOL figures.</p>
          ) : null}
        </div>
      </section>
    </div>
  )
}
